import * as React from 'react';
import { createPortal } from 'react-dom';

/**
 * Position options for the tooltip
 * - top: Tooltip appears above the trigger element
 * - bottom: Tooltip appears below the trigger element
 * - left: Tooltip appears to the left of the trigger element
 * - right: Tooltip appears to the right of the trigger element
 */
type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

/**
 * Props for the Tooltip component
 */
export interface TooltipProps {
  /**
   * The content to display inside the tooltip
   */
  content: React.ReactNode;
  /**
   * The trigger element that the tooltip will be attached to
   * Must be a single React element that can receive event handlers
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: React.ReactElement<any>;
  /**
   * Position of the tooltip relative to the trigger
   * @default 'top'
   */
  position?: TooltipPosition;
  /**
   * Delay in milliseconds before showing the tooltip
   * @default 300
   */
  delay?: number;
  /**
   * Distance in pixels from the trigger element
   * @default 8
   */
  offset?: number;
  /**
   * Whether the tooltip is disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * Additional CSS classes for the tooltip content
   */
  className?: string;
  /**
   * ID for the tooltip element (auto-generated if not provided)
   * Used for aria-describedby
   */
  id?: string;
}

/**
 * Tooltip component for displaying contextual information on hover/focus.
 *
 * Designed for insurance industry applications where additional context
 * helps users understand complex terms, field requirements, or status meanings.
 *
 * @example
 * // Basic usage
 * <Tooltip content="This is additional information">
 *   <button>Hover me</button>
 * </Tooltip>
 *
 * @example
 * // With position and delay
 * <Tooltip content="Policy coverage details" position="right" delay={500}>
 *   <Icon name="info" />
 * </Tooltip>
 *
 * @example
 * // Disabled tooltip
 * <Tooltip content="Won't show" disabled>
 *   <button>No tooltip here</button>
 * </Tooltip>
 */
const Tooltip = React.forwardRef<HTMLDivElement, TooltipProps>(
  (
    {
      content,
      children,
      position = 'top',
      delay = 300,
      offset = 8,
      disabled = false,
      className = '',
      id,
    },
    ref
  ) => {
    const [isVisible, setIsVisible] = React.useState(false);
    const [coords, setCoords] = React.useState({ top: 0, left: 0 });
    const triggerRef = React.useRef<HTMLElement | null>(null);
    const tooltipRef = React.useRef<HTMLDivElement | null>(null);
    const showTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    // Generate a unique ID using React.useId if not provided
    const generatedId = React.useId();
    const tooltipId = id ?? `tooltip${generatedId}`;

    // Calculate tooltip position
    const calculatePosition = React.useCallback(() => {
      /* v8 ignore start */
      if (!triggerRef.current || !tooltipRef.current) return;
      /* v8 ignore stop */

      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const scrollX = window.scrollX;
      const scrollY = window.scrollY;

      let top = 0;
      let left = 0;

      switch (position) {
        case 'top':
          top = triggerRect.top + scrollY - tooltipRect.height - offset;
          left = triggerRect.left + scrollX + (triggerRect.width - tooltipRect.width) / 2;
          break;
        case 'bottom':
          top = triggerRect.bottom + scrollY + offset;
          left = triggerRect.left + scrollX + (triggerRect.width - tooltipRect.width) / 2;
          break;
        case 'left':
          top = triggerRect.top + scrollY + (triggerRect.height - tooltipRect.height) / 2;
          left = triggerRect.left + scrollX - tooltipRect.width - offset;
          break;
        case 'right':
          top = triggerRect.top + scrollY + (triggerRect.height - tooltipRect.height) / 2;
          left = triggerRect.right + scrollX + offset;
          break;
      }

      // Keep tooltip within viewport bounds
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Constrain horizontally
      if (left < scrollX + 8) {
        left = scrollX + 8;
      } else if (left + tooltipRect.width > scrollX + viewportWidth - 8) {
        left = scrollX + viewportWidth - tooltipRect.width - 8;
      }

      // Constrain vertically
      if (top < scrollY + 8) {
        top = scrollY + 8;
      } else if (top + tooltipRect.height > scrollY + viewportHeight - 8) {
        top = scrollY + viewportHeight - tooltipRect.height - 8;
      }

      setCoords({ top, left });
    }, [position, offset]);

    // Show tooltip with delay
    const showTooltip = React.useCallback(() => {
      if (disabled) return;

      showTimeoutRef.current = setTimeout(() => {
        setIsVisible(true);
      }, delay);
    }, [disabled, delay]);

    // Hide tooltip immediately
    const hideTooltip = React.useCallback(() => {
      if (showTimeoutRef.current) {
        clearTimeout(showTimeoutRef.current);
        showTimeoutRef.current = null;
      }
      setIsVisible(false);
    }, []);

    // Handle escape key to dismiss
    React.useEffect(() => {
      const handleEscape = (event: KeyboardEvent) => {
        /* v8 ignore start */
        if (event.key === 'Escape') {
          hideTooltip();
        }
        /* v8 ignore stop */
      };

      if (isVisible) {
        document.addEventListener('keydown', handleEscape);
      }

      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }, [isVisible, hideTooltip]);

    // Recalculate position when visible
    React.useEffect(() => {
      if (isVisible) {
        // Use requestAnimationFrame to ensure the tooltip is rendered before calculating position
        requestAnimationFrame(() => {
          calculatePosition();
        });
      }
    }, [isVisible, calculatePosition]);

    // Cleanup timeout on unmount
    React.useEffect(() => {
      return () => {
        if (showTimeoutRef.current) {
          clearTimeout(showTimeoutRef.current);
        }
      };
    }, []);

    // Get child props safely
    const childProps = children.props as Record<string, unknown>;

    // Arrow classes based on position
    const arrowClasses: Record<TooltipPosition, string> = {
      top: 'bottom-0 left-1/2 -translate-x-1/2 translate-y-full border-l-transparent border-r-transparent border-b-transparent border-t-secondary-900/95',
      bottom:
        'top-0 left-1/2 -translate-x-1/2 -translate-y-full border-l-transparent border-r-transparent border-t-transparent border-b-secondary-900/95',
      left: 'right-0 top-1/2 translate-x-full -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent border-l-secondary-900/95',
      right:
        'left-0 top-1/2 -translate-x-full -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent border-r-secondary-900/95',
    };

    // Combine tooltip classes
    const tooltipClasses = [
      // Base styles
      'fixed z-50',
      'px-2.5 py-1.5',
      'bg-secondary-900/95', // neutral-900 at 95% opacity
      'text-white text-xs',
      'rounded',
      'max-w-[240px]',
      // Animation
      'transition-opacity duration-150',
      isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none',
      // Custom classes
      className,
    ]
      .filter(Boolean)
      .join(' ');

    // Set ref on tooltip element
    const setTooltipRef = React.useCallback(
      (node: HTMLDivElement | null) => {
        tooltipRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref]
    );

    const tooltip = (
      <div
        ref={setTooltipRef}
        id={tooltipId}
        role="tooltip"
        className={tooltipClasses}
        style={{
          top: `${String(coords.top)}px`,
          left: `${String(coords.left)}px`,
        }}
      >
        {content}
        {/* Arrow */}
        <span
          className={`absolute w-0 h-0 border-[6px] border-solid ${arrowClasses[position]}`}
          aria-hidden="true"
        />
      </div>
    );

    // Create the trigger with merged props
    // Using cloneElement to add event handlers and ref
    // eslint-disable-next-line react-hooks/refs
    const trigger = React.cloneElement(children, {
      ref: (node: HTMLElement | null) => {
        triggerRef.current = node;
        // Forward ref from child if it exists
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
        const childRef = (children as any).ref as
          | React.RefCallback<HTMLElement>
          | React.RefObject<HTMLElement | null>
          | null
          | undefined;
        if (typeof childRef === 'function') {
          childRef(node);
        } else if (childRef && typeof childRef === 'object' && 'current' in childRef) {
          // Use Object.assign to update ref.current
          Object.assign(childRef, { current: node });
        }
      },
      onMouseEnter: (event: React.MouseEvent) => {
        showTooltip();
        const handler = childProps.onMouseEnter as
          | ((event: React.MouseEvent) => void)
          | undefined;
        handler?.(event);
      },
      onMouseLeave: (event: React.MouseEvent) => {
        hideTooltip();
        const handler = childProps.onMouseLeave as
          | ((event: React.MouseEvent) => void)
          | undefined;
        handler?.(event);
      },
      onFocus: (event: React.FocusEvent) => {
        showTooltip();
        const handler = childProps.onFocus as ((event: React.FocusEvent) => void) | undefined;
        handler?.(event);
      },
      onBlur: (event: React.FocusEvent) => {
        hideTooltip();
        const handler = childProps.onBlur as ((event: React.FocusEvent) => void) | undefined;
        handler?.(event);
      },
      'aria-describedby': isVisible ? tooltipId : undefined,
    });

    return (
      <>
        {trigger}
        {typeof document !== 'undefined' && createPortal(tooltip, document.body)}
      </>
    );
  }
);

Tooltip.displayName = 'Tooltip';

export { Tooltip };
export type { TooltipPosition };
